import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Building2, Check, ChevronDown, ChevronUp, MapPin, ArrowRight, Phone, Square, Sofa } from 'lucide-react';

const CommercialOfficeCleaning = ({ onQuoteClick }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How often should our office be cleaned?",
      a: "It depends on how busy your office is and how many staff use the space. We can assess your site and recommend daily, weekly or monthly cleaning, and adjust the schedule over time as needed."
    },
    {
      q: "Do you clean carpets and hard floors as part of office cleaning?",
      a: "Yes. Carpet cleaning and hard floor rotary and extraction cleaning are both part of our office cleaning service, along with windows, tiled areas and amenities."
    },
    {
      q: "Can you work around our business hours?",
      a: "Yes, we schedule office cleaning before or after hours to avoid disrupting your team during the working day."
    },
    {
      q: "Do you clean bathrooms and amenities as part of an office clean?",
      a: "Yes, thorough cleaning of tiled areas, toilets and amenities, including complete urinal cleaning, is included."
    }
  ];

  const relatedServices = [
    {
      title: 'Commercial Window Cleaning',
      description: 'Interior and exterior window cleaning for office buildings of any size.',
      link: '/services/commercial-window-cleaning-perth',
      icon: Square
    },
    {
      title: 'Commercial Carpet Cleaning',
      description: 'Deep steam or dry carpet cleaning for office floors.',
      link: '/services/commercial-carpet-cleaning-perth',
      icon: Sofa
    }
  ];

  // Service Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Commercial Office Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://www.extreme-clean.com.au/#organization",
      "name": "Extreme Clean Australia"
    },
    "areaServed": [
      "Perth", "Osborne Park", "Malaga", "Kewdale", "Welshpool", "Canning Vale", 
      "Bibra Lake", "Jandakot", "Wangara", "Balcatta", "Bayswater", "Rockingham", 
      "Mandurah", "Joondalup", "Midland", "Yanchep", "Leederville", "Northbridge", 
      "Mount Lawley", "Fremantle", "Cockburn", "Landsdale", "Hope Valley", 
      "Kalgoorlie", "Esperance", "Albany", "Busselton", "Bunbury", "Margaret River", "Australind", "Geraldton"
    ],
    "description": "Daily, weekly or monthly office cleaning, backed by 20+ years in commercial and industrial cleaning. Commercial window cleaning, carpet cleaning, hard floor rotary and extraction, tiled area cleaning, toilet and amenity cleaning."
  };

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/services/commercial-office-cleaning-perth" />
        <title>Commercial Office Cleaning Perth | Extreme Clean Australia</title>
        <meta name="description" content="Daily, weekly or monthly office cleaning across Perth and WA. Windows, carpets, hard floors, toilets and amenities. 20+ years commercial cleaning experience." />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      <div className="pt-24 lg:pt-44" data-testid="commercial-office-page">
        {/* Hero - Full width background style */}
        <section className="bg-[#0A0A0A] py-24 relative" data-testid="service-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85"
              alt="Modern commercial office interior"
              className="w-full h-full object-cover opacity-30"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-[#3B82F6] bg-opacity-20 px-4 py-2 mb-6">
                <Building2 size={20} className="text-[#3B82F6]" />
                <span className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Office Cleaning</span>
              </div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl !leading-[1.2] mb-6 text-white">
                Commercial Office Cleaning Perth
              </h1>
              <p className="text-xl text-gray-300 mb-4">
                Daily, weekly or monthly office cleaning, backed by 20+ years in commercial and industrial cleaning
              </p>
              <p className="text-gray-400 mb-8">
                A clean office says something about how a business operates, and it matters to staff and clients alike. Extreme Clean Australia brings the same standard we apply to commercial kitchens and industrial sites to office cleaning, scheduled around your business, whether that's daily, weekly or monthly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onQuoteClick}
                  className="bg-[#3B82F6] hover:bg-blue-600 text-white uppercase font-bold tracking-widest px-8 py-4 text-sm transition-all flex items-center justify-center gap-2"
                >
                  Get Free Quote <ArrowRight size={18} />
                </button>
                <a
                  href="tel:0861875523"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
                >
                  <Phone size={18} /> Call 08 6187 5523
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scope</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">What's Included</h2>
              <p className="text-gray-600 mb-8">
                Office cleaning covers what an occupied workplace actually needs: commercial window cleaning, carpet cleaning, hard floor rotary and extraction cleaning, and thorough cleaning of tiled areas, toilets and amenities, including complete urinal cleaning. Scope is built around your office and your schedule, not a fixed package.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Commercial window cleaning',
                  'Carpet cleaning',
                  'Hard floor rotary & extraction',
                  'Tiled area cleaning',
                  'Toilet & amenity cleaning',
                  'Complete urinal cleaning'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Check size={20} className="text-[#3B82F6] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Scheduled to Suit Your Business */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Scheduling</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Scheduled to Suit Your Business</h2>
              <p className="text-gray-600 mb-8">
                Office cleaning works best as a recurring service, and we schedule around what your business actually needs, daily, weekly, or monthly, fitting in before or after hours to avoid disrupting your team.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Daily', 'Weekly', 'Monthly', 'Before Hours', 'After Hours'].map((schedule, index) => (
                  <span key={index} className="bg-white border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                    {schedule}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Built on Real Experience */}
        <section className="py-20 bg-[#273268]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Experience</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6 text-white">Built on Real Industrial and Commercial Experience</h2>
              <p className="text-gray-300">
                Office cleaning is a newer part of our service offering, extending the same standards we've built over 20+ years cleaning commercial kitchens, industrial sites and warehouses across WA. The same attention to detail and reliability applies, whether it's a compliance-critical kitchen exhaust system or a busy office floor.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">FAQ</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Common Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div 
                    key={index} 
                    className="border border-gray-200 bg-gray-50"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left"
                      aria-expanded={openFaq === index}
                    >
                      <span className="font-heading font-bold text-lg pr-4">{faq.q}</span>
                      {openFaq === index ? (
                        <ChevronUp size={20} className="text-[#3B82F6] flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Areas We Service */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin size={20} className="text-[#3B82F6]" />
                <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider">Service Areas</p>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-6">Areas We Service</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Perth Metro</h3>
                  <p className="text-gray-600">
                    We service the entire Perth metro area, from Yanchep in the north to Mandurah in the south, including Osborne Park, Malaga, Kewdale, Welshpool, Canning Vale, Bibra Lake, Jandakot, Wangara, Balcatta, Bayswater, Rockingham, Joondalup and Midland.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-heading font-bold text-xl mb-3">Regional WA</h3>
                  <p className="text-gray-600">
                    Kalgoorlie, Esperance, Albany, Busselton, Bunbury, Margaret River, Australind, Geraldton.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-[#3B82F6] font-mono text-sm uppercase tracking-wider mb-2">Related</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-8">Related Services</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedServices.map((service, index) => (
                  <Link 
                    key={index}
                    to={service.link}
                    className="block bg-gray-50 p-6 border border-gray-200 hover:border-[#3B82F6] hover:shadow-md transition-all group"
                  >
                    <div className="w-12 h-12 bg-blue-50 mb-4 flex items-center justify-center group-hover:bg-[#3B82F6] transition-colors">
                      <service.icon size={24} className="text-[#3B82F6] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-lg mb-2 group-hover:text-[#3B82F6] transition-colors">{service.title}</h3>
                    <p className="text-gray-600 text-sm">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-[#3B82F6]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to Discuss Your Office Cleaning Needs?
            </h2>
            <p className="text-blue-100 text-lg mb-8">
              Get a free quote for daily, weekly or monthly office cleaning.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onQuoteClick}
                className="bg-white text-[#0A0A0A] hover:bg-gray-100 transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm"
              >
                Get Free Quote
              </button>
              <a
                href="tel:0861875523"
                className="border-2 border-white text-white hover:bg-white hover:text-[#3B82F6] transition-all uppercase font-bold tracking-widest px-8 py-4 text-sm flex items-center justify-center gap-2"
              >
                <Phone size={18} /> Call 08 6187 5523
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CommercialOfficeCleaning;
